import {
  IMarketingDrillState,
  IMarketingRatioView,
  IMarketingSortOf,
  IMarketingSpendView,
  isMarketingChannel,
  isMarketingRatioKey,
  isMarketingRootDimension,
  marketingCanDrillRow,
  marketingChannelFromKey,
  marketingConversion,
  marketingDrillState,
  marketingNumber,
  marketingRatioView,
  marketingRowSeriesFilter,
  marketingShownValue,
  marketingSpendView,
  MARKETING_PAID_CHANNELS,
} from './marketingModel';
import { isMarketingVisitorId } from './marketingJourneyModel';
import { MARKETING_DOMAIN_RE } from './marketingSeoModel';
import {
  IMarketingBreakdownRow,
  IMarketingDemandRow,
  IMarketingFilter,
  IMarketingForecastRow,
  IMarketingMetrics,
  IMarketingSaleRow,
  MarketingChannelCode,
  MarketingDimensionCode,
} from './types';

/**
 * ТЗ 0088, доводка читаемости — «панель справа» раздела «Маркетинг»: клик по
 * строке открывает подробности сущности, не уводя со страницы. Здесь — чистая
 * часть: что лежит в адресе (`insp=<вид>:<ключ>`), какие запросы нужны панели
 * сущности, куда ведёт «Спуститься в таблице», полосы «Сколько вложили и
 * сколько вернулось» и строки таблицы сравнения курсов. Без React, Apollo и
 * i18n — подписи ключами `marketing.*`.
 */

/* ── Вид и ключ в адресе ─────────────────────────────────────────────── */

/**
 * Виды панели. `ad` — объявление или группа ассетов PMax (`AdInspector`),
 * `sale` — продажа «Качества данных» (`SaleInspector`), `ai` — AI-ассистент
 * «Поиска и сайта» (`AiInspector`): у двух последних своей строки разреза
 * нет, данные панели — строки таблицы своей вкладки.
 */
export const MARKETING_INSPECTOR_KINDS = [
  'channel',
  'campaign',
  'adset',
  'ad',
  'family',
  'offer',
  'sale',
  'ai',
  // ТЗ 0103, этап 3: анонимный посетитель (`VisitorInspector`, таймлайн) и
  // страница сайта (`PageInspector`, ключ — путь).
  'visitor',
  'page',
  // ТЗ 0103, этап 4 — SEO: запрос (`KeywordInspector`, ключ — id), домен
  // конкурента (`CompetitorInspector`, ключ — домен) и вопрос AI
  // (`AiAnswerInspector`, ключ — id).
  'keyword',
  'competitor',
  'aiq',
] as const;

export type MarketingInspectorKind = typeof MARKETING_INSPECTOR_KINDS[number];

export const isMarketingInspectorKind = (
  value: string | null | undefined,
): value is MarketingInspectorKind =>
  Boolean(value) &&
  (MARKETING_INSPECTOR_KINDS as readonly string[]).includes(value as string);

export interface IMarketingInspectorTarget {
  kind: MarketingInspectorKind;
  /** Код канала (`META`), id сущности, slug курса, код оффера… */
  key: string;
}

/** Длиннее — это мусор, а не ключ: в запрос его не везём. */
export const MARKETING_INSPECTOR_KEY_MAX = 200;

/** Путь страницы длиннее (справочник страниц сервера — до 500 знаков). */
export const MARKETING_INSPECTOR_PAGE_KEY_MAX = 500;

/**
 * `insp` адреса → вид и ключ. Ключ может содержать двоеточие (имя
 * AI-ассистента), поэтому режем по ПЕРВОМУ. Незнакомый вид, пустой или
 * слишком длинный ключ — `null`: панель закрыта, экран не падает. Канал
 * приводится к коду enum (`meta` → `META`), как и в спуске.
 */
export const parseMarketingInspector = (
  raw: string | null | undefined,
): IMarketingInspectorTarget | null => {
  const value = (raw ?? '').trim();
  const at = value.indexOf(':');
  if (at <= 0) return null;
  const kind = value.slice(0, at);
  const key = value.slice(at + 1).trim();
  if (!isMarketingInspectorKind(kind)) return null;
  const max =
    kind === 'page'
      ? MARKETING_INSPECTOR_PAGE_KEY_MAX
      : MARKETING_INSPECTOR_KEY_MAX;
  if (!key || key.length > max) return null;
  // Страница — путь сайта (`/courses/x`); посетитель — 16 знаков hex (не
  // порядковый номер, ревью 0103 S5); запрос и вопрос AI — числа.
  if (kind === 'page' && !key.startsWith('/')) return null;
  if (kind === 'visitor') {
    const id = key.toLowerCase();
    return isMarketingVisitorId(id) ? { kind, key: id } : null;
  }
  if ((kind === 'keyword' || kind === 'aiq') && !/^\d+$/.test(key)) {
    return null;
  }
  if (kind === 'competitor') {
    const domain = key.toLowerCase();
    return MARKETING_DOMAIN_RE.test(domain) ? { kind, key: domain } : null;
  }
  return { kind, key: kind === 'channel' ? marketingChannelFromKey(key) : key };
};

export const buildMarketingInspector = (
  target: IMarketingInspectorTarget,
): string => `${target.kind}:${target.key}`;

export const marketingSameInspector = (
  a: IMarketingInspectorTarget | null | undefined,
  b: IMarketingInspectorTarget | null | undefined,
): boolean =>
  Boolean(a) && Boolean(b) && a?.kind === b?.kind && a?.key === b?.key;

/* ── Вид ↔ разрез таблицы ────────────────────────────────────────────── */

/** Виды, у которых есть своя строка в `marketingBreakdown`. */
const KIND_DIMENSION: Readonly<
  Partial<Record<MarketingInspectorKind, MarketingDimensionCode>>
> = {
  channel: 'CHANNEL',
  campaign: 'CAMPAIGN',
  adset: 'ADSET',
  ad: 'AD',
  family: 'FAMILY',
  offer: 'OFFER',
};

const DIMENSION_KIND: Readonly<
  Record<MarketingDimensionCode, MarketingInspectorKind>
> = {
  CHANNEL: 'channel',
  CAMPAIGN: 'campaign',
  ADSET: 'adset',
  AD: 'ad',
  FAMILY: 'family',
  OFFER: 'offer',
  // Строка формата (`video`) — не сущность: ключ не число, и панель
  // объявления её не откроет (`marketingInspectorRowOpenable` — нет).
  FORMAT: 'ad',
};

export const marketingInspectorDimension = (
  kind: MarketingInspectorKind,
): MarketingDimensionCode | null => KIND_DIMENSION[kind] ?? null;

/** Вид панели строки разреза: кампания → `campaign`… */
export const marketingInspectorKindOf = (
  dimension: MarketingDimensionCode,
): MarketingInspectorKind => DIMENSION_KIND[dimension];

/** Виды, подробности которых рисует общая панель сущности (`EntityInspector`). */
export const MARKETING_ENTITY_KINDS: MarketingInspectorKind[] = [
  'channel',
  'campaign',
  'adset',
  'family',
  'offer',
];

export const marketingIsEntityKind = (kind: MarketingInspectorKind): boolean =>
  MARKETING_ENTITY_KINDS.includes(kind);

const NUMERIC_ID_RE = /^\d+$/;

/**
 * Годится ли ключ в запрос. Кампания, группа и объявление — числа (сервер
 * иначе откажет всем запросом), канал — из словаря. Негодный ключ — не ошибка
 * экрана, а «не найдено за это окно».
 */
export const marketingInspectorKeyValid = (
  target: IMarketingInspectorTarget,
): boolean => {
  switch (target.kind) {
    case 'channel':
      return isMarketingChannel(target.key);
    case 'campaign':
    case 'adset':
    case 'ad':
    case 'sale':
      return NUMERIC_ID_RE.test(target.key);
    default:
      return Boolean(target.key);
  }
};

/**
 * Каналы с объявлениями — у них есть «Креативы» (площадки сбора креативов:
 * Meta и Google Ads).
 */
export const MARKETING_CREATIVE_CHANNELS: MarketingChannelCode[] = [
  'META',
  'GOOGLE_ADS',
];

/**
 * Можно ли открыть панель по строке разреза. Кампания, группа и объявление
 * «не определено» — не сущность: у них нет id.
 */
export const marketingInspectorRowOpenable = (
  dimension: MarketingDimensionCode,
  key: string,
): boolean =>
  marketingInspectorKeyValid({
    kind: marketingInspectorKindOf(dimension),
    key,
  });

/* ── Что запрашивает панель сущности ─────────────────────────────────── */

/** Строк в короткой разбивке панели. */
export const MARKETING_INSPECTOR_BLOCK_ROWS = 8;

export const MARKETING_INSPECTOR_BLOCK_IDS = [
  'campaigns',
  'adsets',
  'ads',
  'courses',
  'channels',
] as const;

export type MarketingInspectorBlockId = typeof MARKETING_INSPECTOR_BLOCK_IDS[number];

export interface IMarketingInspectorBlockPlan {
  id: MarketingInspectorBlockId;
  dimension: MarketingDimensionCode;
  filter: IMarketingFilter;
  parentKey: string | null;
  /**
   * Если основная разбивка пришла пустой — показать эту (у кампании без
   * групп объявлений — сами объявления).
   */
  fallback: IMarketingInspectorBlockPlan | null;
}

export interface IMarketingInspectorPlan {
  /** Строка самой сущности: её разрез, фильтр, суженный до неё. */
  own: { dimension: MarketingDimensionCode; filter: IMarketingFilter };
  /** Фильтр недельного ряда — тот же суженный. */
  seriesFilter: IMarketingFilter;
  /** Короткие разбивки (по 8 строк), не больше двух. */
  blocks: IMarketingInspectorBlockPlan[];
}

/**
 * Запросы панели сущности. Фильтр — фильтр вкладки (на «Каналах» — уже
 * суженный спуском, чтобы панель показывала ту же строку, что и таблица),
 * суженный ключом сущности ровно так, как это делает спуск.
 *
 *  - канал → кампании (только у платных: у органики кампаний нет) и курсы;
 *  - кампания → группы (пусто — объявления) и курсы;
 *  - группа → объявления;
 *  - курс → каналы и кампании;
 *  - оффер → каналы.
 *
 * `null` — сущность не из этого набора или её ключ не годится в запрос.
 */
export const marketingInspectorPlan = (
  target: IMarketingInspectorTarget,
  base: IMarketingFilter,
): IMarketingInspectorPlan | null => {
  if (!marketingIsEntityKind(target.kind)) return null;
  if (!marketingInspectorKeyValid(target)) return null;
  const dimension = marketingInspectorDimension(target.kind);
  if (!dimension) return null;
  const narrowed = marketingRowSeriesFilter(base, dimension, target.key);
  if (!narrowed) return null;
  const block = (
    id: MarketingInspectorBlockId,
    blockDimension: MarketingDimensionCode,
    parentKey: string | null = null,
    fallback: IMarketingInspectorBlockPlan | null = null,
  ): IMarketingInspectorBlockPlan => ({
    id,
    dimension: blockDimension,
    filter: narrowed,
    parentKey,
    fallback,
  });
  let blocks: IMarketingInspectorBlockPlan[];
  switch (target.kind) {
    case 'channel':
      blocks = [
        ...((MARKETING_PAID_CHANNELS as string[]).includes(target.key)
          ? [block('campaigns', 'CAMPAIGN', target.key)]
          : []),
        block('courses', 'FAMILY'),
      ];
      break;
    case 'campaign':
      blocks = [
        block('adsets', 'ADSET', target.key, block('ads', 'AD')),
        block('courses', 'FAMILY'),
      ];
      break;
    case 'adset':
      blocks = [block('ads', 'AD', target.key)];
      break;
    case 'family':
      blocks = [block('channels', 'CHANNEL'), block('campaigns', 'CAMPAIGN')];
      break;
    default:
      blocks = [block('channels', 'CHANNEL')];
  }
  return {
    own: { dimension, filter: narrowed },
    seriesFilter: narrowed,
    blocks,
  };
};

/**
 * Строка сущности в ответе своего разреза. Ключ канала сравнивается кодом
 * enum (ответ уже нормализован доменным хуком).
 */
export const marketingInspectorRow = (
  target: IMarketingInspectorTarget,
  rows: ReadonlyArray<IMarketingBreakdownRow> | null | undefined,
): IMarketingBreakdownRow | null =>
  (rows ?? []).find((row) => row.key === target.key) ?? null;

/* ── «Спуститься в таблице» ──────────────────────────────────────────── */

/** Куда спуститься в таблице «Каналы»: корень разреза и путь ключей. */
export interface IMarketingDrillTarget {
  dimension: MarketingDimensionCode;
  path: string[];
}

/**
 * Есть ли смысл спускаться в строку: следующий уровень существует, и он не
 * пустой по построению. У неплатного канала (органика, звонок, email…)
 * кампаний нет — спуск показал бы одну строку «не определено».
 */
export const marketingDrillUseful = (
  dimension: MarketingDimensionCode,
  key: string,
): boolean =>
  marketingCanDrillRow(dimension, key) &&
  (dimension !== 'CHANNEL' ||
    (MARKETING_PAID_CHANNELS as string[]).includes(key));

/**
 * Путь годится, только если спуск проходит его целиком и последний шаг
 * ведёт не в пустоту (`marketingDrillUseful`).
 */
export const marketingValidDrill = (
  drill: IMarketingDrillTarget | null | undefined,
): IMarketingDrillTarget | null => {
  if (!drill || !drill.path.length) return null;
  if (!isMarketingRootDimension(drill.dimension)) return null;
  const { steps } = marketingDrillState(drill.dimension, drill.path);
  if (steps.length !== drill.path.length) return null;
  const last = steps[steps.length - 1];
  return marketingDrillUseful(last.dimension, last.key) ? drill : null;
};

export interface IMarketingInspectorDrillContext {
  /** Запомнено при клике (строка таблицы, строка короткой разбивки). */
  remembered?: IMarketingDrillTarget | null;
  /**
   * Таблица «Каналов», если панель открыта на этой вкладке: сущность того же
   * вида, что строки таблицы, — строка этой таблицы.
   */
  table?: {
    root: MarketingDimensionCode;
    path: ReadonlyArray<string>;
    drill: IMarketingDrillState;
  } | null;
  /** Площадка кампании (`meta`) — канал её спуска. */
  platform?: string | null;
}

/**
 * Путь «Спуститься в таблице» для сущности панели: запомненный при клике
 * (или, по свежей ссылке, строка текущей таблицы) → канонический от корня (канал, курс, оффер —
 * сразу; кампания — через канал своей площадки). Группе объявлений без
 * запомненного пути спускаться некуда (кампания неизвестна) — `null`, как и
 * объявлению (глубже спуска нет).
 */
export const marketingInspectorDrill = (
  target: IMarketingInspectorTarget,
  { remembered, table, platform }: IMarketingInspectorDrillContext = {},
): IMarketingDrillTarget | null => {
  if (remembered !== undefined) {
    // Открыто кликом: запомненный путь главнее; нет его — канонический.
    // Таблицу не спрашиваем: строка разбивки панели — не строка таблицы.
    const valid = marketingValidDrill(remembered);
    if (valid) return valid;
  } else if (
    table &&
    marketingInspectorKindOf(table.drill.dimension) === target.kind
  ) {
    // Свежая ссылка на вкладке «Каналы»: сущность вида строк таблицы — её
    // строка, путь продолжает текущий.
    const fromTable = marketingValidDrill({
      dimension: table.root,
      path: [...table.path, target.key],
    });
    if (fromTable) return fromTable;
  }
  switch (target.kind) {
    case 'channel':
      return marketingValidDrill({ dimension: 'CHANNEL', path: [target.key] });
    case 'family':
      return marketingValidDrill({ dimension: 'FAMILY', path: [target.key] });
    case 'offer':
      return marketingValidDrill({ dimension: 'OFFER', path: [target.key] });
    case 'campaign': {
      const channel = platform ? marketingChannelFromKey(platform) : '';
      return isMarketingChannel(channel)
        ? marketingValidDrill({
            dimension: 'CHANNEL',
            path: [channel, target.key],
          })
        : null;
    }
    default:
      return null;
  }
};

/**
 * Что страница помнит о сущности, открытой кликом: подпись строки (имя в
 * шапке, пока едет ответ) и путь спуска. После перезагрузки памяти нет —
 * тогда путь канонический (`marketingInspectorDrill`), имя — с сервера.
 */
export interface IMarketingInspectorMemo {
  label?: string;
  /** `null` — открыто кликом, но своего пути нет (канонический). */
  drill?: IMarketingDrillTarget | null;
  /**
   * Продажа, открытая кликом: строка таблицы на момент клика. Панель берёт
   * свежую строку из загруженной страницы, а эту — когда таблицу пролистали
   * и строки на странице больше нет.
   */
  sale?: IMarketingSaleRow;
}

/**
 * Путь строки короткой разбивки: если она — следующий уровень спуска
 * родителя (канал → его кампании → их группы), путь продолжается; иначе
 * `null` (курс в панели канала спускается от своего корня).
 */
export const marketingInspectorChildDrill = (
  parent: IMarketingDrillTarget | null,
  childDimension: MarketingDimensionCode,
  childKey: string,
): IMarketingDrillTarget | null => {
  if (!parent) return null;
  const { dimension } = marketingDrillState(parent.dimension, parent.path);
  if (dimension !== childDimension) return null;
  return marketingValidDrill({
    dimension: parent.dimension,
    path: [...parent.path, childKey],
  });
};

/* ── «Сколько вложили и сколько вернулось» ───────────────────────────── */

export interface IMarketingMoneyRow {
  /** Код канала (`META`). */
  key: string;
  /** Расход для показа: полный или за собранные дни. */
  spend: IMarketingSpendView;
  revenue: number | null;
  cash: number | null;
  /**
   * ROAS для показа: полный или за собранные дни (приглушённо, «по ДД.ММ»);
   * `value: null` — не знаем.
   */
  roas: IMarketingRatioView;
  /** У канала есть рекламный бюджет (платный или с расходом). */
  paid: boolean;
  /** Длина полос — доля общего максимума, 0..100. */
  spendPct: number;
  revenuePct: number;
  cashPct: number;
}

const pctOfMax = (value: number | null, max: number): number =>
  value === null || max <= 0
    ? 0
    : Math.max(0, Math.min(100, (value / max) * 100));

/**
 * Полосы «расход против подписанного и поступившего» по каналам — из тех же
 * строк разреза, без нового запроса. Шкала одна на все каналы (иначе полосы
 * разных каналов не сравнить), по убыванию подписанного: сверху то, что
 * приносит больше денег. Канал без расхода — только выручка и пометка «без
 * рекламного бюджета», а не нулевая полоса расхода.
 */
export const marketingMoneyRows = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
): IMarketingMoneyRow[] => {
  const base = rows.map((row) => {
    const spend = marketingSpendView(row.metrics);
    return {
      key: row.key,
      spend,
      revenue: marketingNumber(row.metrics.revenuePln),
      cash: marketingNumber(row.metrics.cashPln),
      roas: marketingRatioView(row.metrics, 'roas'),
      paid:
        (MARKETING_PAID_CHANNELS as string[]).includes(row.key) ||
        (spend.value !== null && spend.value > 0),
    };
  });
  const max = base.reduce(
    (acc, row) =>
      Math.max(acc, row.spend.value ?? 0, row.revenue ?? 0, row.cash ?? 0),
    0,
  );
  return base
    .map((row) => ({
      ...row,
      spendPct: pctOfMax(row.spend.value, max),
      revenuePct: pctOfMax(row.revenue, max),
      cashPct: pctOfMax(row.cash, max),
    }))
    .sort(
      (a, b) =>
        (b.revenue ?? 0) - (a.revenue ?? 0) ||
        (b.spend.value ?? 0) - (a.spend.value ?? 0) ||
        a.key.localeCompare(b.key),
    );
};

/* ── Таблица сравнения курсов ────────────────────────────────────────── */

export type MarketingCourseSortKey =
  | 'label'
  | 'crmLeads'
  | 'sales'
  | 'conversion'
  | 'contractsSent'
  | 'spendPln'
  | 'cplPln'
  | 'costPerSalePln'
  | 'revenuePln'
  | 'roas'
  | 'demand'
  | 'forecast';

export type IMarketingCourseSort = IMarketingSortOf<MarketingCourseSortKey>;

/** По умолчанию — продажи по убыванию: сверху курс, который продаётся. */
export const MARKETING_COURSES_DEFAULT_SORT: IMarketingCourseSort = {
  key: 'sales',
  desc: true,
};

/** Ключ курса «не определён» — как у всего раздела. */
const NONE_KEY = 'none';

export interface IMarketingCourseRow {
  /** Slug семейства; `none` — курс не определён. */
  key: string;
  /** Имя с сервера (у «не определён» — пусто, подпись даёт экран). */
  label: string;
  /** `null` — у курса в окне нет активности, есть только прогноз. */
  metrics: IMarketingMetrics | null;
  /** ТЗ 0103: тот же курс за окно сравнения сервера; нет — без сравнения. */
  previous?: IMarketingMetrics | null;
  demand: IMarketingDemandRow | null;
  forecast: IMarketingForecastRow | null;
  /**
   * Приглушённая строка внизу: курс не определён или у курса только
   * прогноз. Сортировка их не поднимает — сравнивать их с курсами не с чем.
   */
  muted: boolean;
}

export interface IMarketingCourseRowsOptions {
  /**
   * Строки ДО быстрого фильтра: «курс без активности» решается по ним, а не
   * по отфильтрованным (иначе при «Растут» все курсы вне фильтра выглядели
   * бы «только прогнозом»).
   */
  allRows?: ReadonlyArray<IMarketingBreakdownRow> | null;
  /**
   * Активен быстрый фильтр (`qf`): строк «только прогноз» не добавляем —
   * у них нет метрик, фильтру они не подчиняются (повторное ревью 0103, A1).
   */
  filtered?: boolean;
}

/**
 * Строки таблицы курсов: разрез по семействам + спрос и прогноз по slug.
 * Курс, у которого в окне нет ни лида, ни продажи, но есть прогноз месяца, —
 * своей строкой (иначе прогноз продаваемого курса молча пропадал бы). При
 * быстром фильтре таких строк нет: фильтр — по метрикам окна.
 */
export const marketingCourseRows = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
  demandByFamily?: Readonly<Record<string, IMarketingDemandRow>> | null,
  forecastByFamily?: Readonly<Record<string, IMarketingForecastRow>> | null,
  options: IMarketingCourseRowsOptions = {},
): IMarketingCourseRow[] => {
  const shown = new Set((options.allRows ?? rows).map((row) => row.key));
  const main = rows.map((row) => ({
    key: row.key,
    label: row.label,
    metrics: row.metrics,
    previous: row.previous ?? null,
    demand: demandByFamily?.[row.key] ?? null,
    forecast: forecastByFamily?.[row.key] ?? null,
    muted: row.key === NONE_KEY,
  }));
  if (options.filtered) return main;
  const forecastOnly = Object.values(forecastByFamily ?? {})
    .filter((forecast) => !shown.has(forecast.familySlug))
    .map((forecast) => ({
      key: forecast.familySlug,
      label: forecast.familyName,
      metrics: null,
      demand: demandByFamily?.[forecast.familySlug] ?? null,
      forecast,
      muted: true,
    }));
  return [...main, ...forecastOnly];
};

/** Значение столбца для сортировки; `null` — пропуск (всегда в конце). */
export const marketingCourseSortValue = (
  row: IMarketingCourseRow,
  key: MarketingCourseSortKey,
): number | string | null => {
  switch (key) {
    case 'label':
      return (row.label || row.key).toLocaleLowerCase();
    case 'conversion':
      return row.metrics
        ? marketingConversion(row.metrics.sales, row.metrics.crmLeads)
        : null;
    case 'demand':
      return row.demand ? row.demand.impressions : null;
    case 'forecast':
      return row.forecast ? row.forecast.p50 : null;
    case 'spendPln':
      // Расход окна, которое кончается сегодня, почти всегда собран не
      // целиком: сортируем по показанному (за собранные дни), иначе столбец
      // из одних «частичных» не сортировался бы вовсе. Так же — CPL,
      // стоимость продажи и ROAS (за собранные дни, когда основных нет).
      return row.metrics ? marketingSpendView(row.metrics).value : null;
    default:
      if (isMarketingRatioKey(key)) {
        return row.metrics ? marketingShownValue(row.metrics, key) : null;
      }
      return row.metrics ? marketingNumber(row.metrics[key]) : null;
  }
};

const compareValues = (
  a: number | string | null,
  b: number | string | null,
  desc: boolean,
): number => {
  if (a === null && b === null) return 0;
  // Пропуск — в конце в любом направлении (как у сервера): «—» наверху
  // выглядел бы лучшим курсом.
  if (a === null) return 1;
  if (b === null) return -1;
  const order =
    typeof a === 'string' || typeof b === 'string'
      ? String(a).localeCompare(String(b))
      : a - b;
  return desc ? -order : order;
};

/**
 * Сортировка таблицы курсов: сначала обычные строки по столбцу, потом
 * приглушённые (не определён, только прогноз) — тем же порядком, но всегда
 * внизу. Равные — по имени, чтобы порядок не прыгал между рендерами.
 */
export const marketingSortCourseRows = (
  rows: ReadonlyArray<IMarketingCourseRow>,
  sort: IMarketingCourseSort | null,
): IMarketingCourseRow[] => {
  const active = sort ?? MARKETING_COURSES_DEFAULT_SORT;
  const byName = (a: IMarketingCourseRow, b: IMarketingCourseRow) =>
    (a.label || a.key).localeCompare(b.label || b.key);
  const sorted = [...rows].sort(
    (a, b) =>
      compareValues(
        marketingCourseSortValue(a, active.key),
        marketingCourseSortValue(b, active.key),
        active.desc,
      ) || byName(a, b),
  );
  return [
    ...sorted.filter((row) => !row.muted),
    ...sorted.filter((row) => row.muted),
  ];
};
